import { useEffect } from 'react';

import Button from '../../generic/Button/Button';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import SocialLoginButtons from '../components/SocialLoginButtons';
import useResolveUser from '../hooks/useResolveUser';
import type { Candidate, OAuthProvider, ResolveUserResponse } from '../../../../shared/types/routes/auth/v2';

import styles from '../Login.module.less';

export type EnterIdentifierStepProps = {
  identifier: string;
  onIdentifierChange: (value: string) => void;
  oauthProviders: OAuthProvider[];
  /**
   * When set, the flow is locked to this org (a `?subdomain=` landing): submit skips resolve-user and goes
   * straight to the org's methods, so any identifier reaches the credential form (e.g. an LDAP first login).
   */
  subdomainOrg?: Candidate | null;
  /** Post-login redirect from a `?next=` deep link, threaded onto the social-login entry so it survives the round-trip. */
  next?: string;
  onResolved: (response: ResolveUserResponse) => void;
};

const EnterIdentifierStep = ({
  identifier,
  onIdentifierChange,
  oauthProviders,
  subdomainOrg,
  next,
  onResolved,
}: EnterIdentifierStepProps) => {
  const resolveUser = useResolveUser();

  // Advance the flow from an effect once resolution lands, rather than the mutate `onSuccess` callback. React Query
  // runs that callback before notifying its subscribers, so navigating there unmounts this step while React Query is
  // still about to update it — which warns about a state update on an unmounted component under React 17. Reacting
  // to the settled `data` here defers the transition until after this step has re-rendered, sidestepping the race.
  useEffect(() => {
    if (resolveUser.data) {
      onResolved(resolveUser.data);
    }
  }, [resolveUser.data, onResolved]);

  // Locked to a subdomain org, its `allowedStrategies` is the authoritative per-org policy for which
  // social providers may complete a login here.
  const socialProviders = subdomainOrg
    ? oauthProviders.filter(provider => subdomainOrg.allowedStrategies.includes(provider))
    : oauthProviders;

  const handleSubmit = () => {
    if (!identifier || resolveUser.isLoading) {
      return;
    }
    if (subdomainOrg) {
      // Locked to the subdomain org: skip resolve-user (which matches the identifier to member orgs) and
      // advance straight to its methods, so an unknown identifier still reaches the org's credential form.
      onResolved({ candidates: [subdomainOrg], signupSuggestions: [], hasOrphanAccount: false });
      return;
    }
    resolveUser.mutate({ identifier, context: 'login' });
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>{subdomainOrg ? `Log in to ${subdomainOrg.organizationName}` : 'Sign In'}</h1>
      <div className={styles.fieldGroup}>
        <LabeledTextInput
          label="Email or username"
          // type="text" rather than "email" so the browser does not reject a bare username.
          type="text"
          name="identifier"
          placeholder="Enter your username or email"
          value={identifier}
          autoFocus
          isRequired
          onChange={onIdentifierChange}
        />
        <ForgotPasswordLink />
      </div>
      {/* onClick, not a submit button: Enter submits through the form's onSubmit, and a non-submit button
          avoids a double-fire while still working under jsdom, which doesn't dispatch native submit on click. */}
      <Button isFullWidth size="large" isDisabled={resolveUser.isLoading} onClick={handleSubmit}>
        Next
      </Button>
      {/* In subdomain mode, hide the personal social logins only when the org requires SSO (disable_invite) —
          they'd break that lock. A non-SSO subdomain org still offers them, filtered through its allowed
          strategies so no button leads to a login the policy gate would reject (mirrors MethodsStep). Without
          a subdomain org there is no org-scoped policy yet, so every configured provider is offered. */}
      {!subdomainOrg?.requiresSso && socialProviders.length > 0 && (
        <SocialLoginButtons providers={socialProviders} next={next} />
      )}
    </form>
  );
};

export default EnterIdentifierStep;
